import { sanitizeRichHtml } from './sanitizeRichHtml.js';
import { quillListsToSemanticHtml } from './quillSemanticLists.js';

/** Quill empty doc / whitespace-only → '' (matches editor behavior). */
export function normalizeQuillEmpty(html) {
  let s = html == null ? '' : String(html).trim();
  if (/^<p><br\s*\/?><\/p>$/i.test(s)) s = '';
  return s;
}

/** Treat Quill “empty” document as blank, then markdown links + sanitize. */
export function normalizeQuillHtml(html) {
  let s = normalizeQuillEmpty(html);
  if (!s) return '';

  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  s = quillListsToSemanticHtml(s);

  return sanitizeRichHtml(s);
}

/** True when HTML includes a data table (Quill often drops these on controlled sync). */
export function htmlHasTable(html) {
  return /<table\b/i.test(String(html || ''));
}

/**
 * Normalize API / legacy HTML before loading into Quill (decode entities, strip document shell).
 */
export function prepareRichHtmlForEditor(html) {
  let s = String(html ?? '').trim();
  if (!s) return '';

  if (!/<[a-z][\w:-]*\b/i.test(s) && /&lt;|&gt;|&amp;#/.test(s)) {
    if (typeof document !== 'undefined') {
      const ta = document.createElement('textarea');
      ta.innerHTML = s;
      s = ta.value.trim();
    }
  }

  // Strip empty list items that may have accumulated in old HTML
  s = s.replace(/<li[^>]*>(?:<span[^>]*><\/span>)?(?:<br\s*\/?>|\s|&nbsp;)*<\/li>/gi, '');

  // Strip quill-table-better specific attributes that confuse Quill 2 native tables
  s = s.replace(/\sdata-row="[^"]*"/gi, '');
  s = s.replace(/\sdata-cell="[^"]*"/gi, '');
  s = s.replace(/\sclass="ql-table-block"/gi, '');
  
  const fragment = s.match(/<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/i);
  if (fragment) s = fragment[1].trim();

  if (/<\s*html[\s>]/i.test(s) && typeof DOMParser !== 'undefined') {
    try {
      const body = new DOMParser().parseFromString(s, 'text/html').body;
      if (body?.innerHTML?.trim()) s = body.innerHTML.trim();
    } catch {
      /* keep original */
    }
  }

  return s;
}

/** True when HTML has visible text or tables (Quill 2 may emit empty on load for legacy HTML). */
export function htmlHasRichContent(html) {
  const s = String(html || '').trim();
  if (!s) return false;
  if (htmlHasTable(s)) return true;
  const text = s
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ' ')
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 0;
}

/**
 * Pick policy/modal HTML for save: prefer live editor, keep API backup when Quill emptied tables.
 */
export function resolvePolicyRichHtmlForSave({ liveHtml = '', formHtml = '', backupHtml = '' } = {}) {
  const live = normalizeQuillEmpty(liveHtml) ? String(formHtml || '') : String(liveHtml || '');
  let out = normalizeQuillHtml(live);
  const backup = String(backupHtml || formHtml || '');
  if (!htmlHasRichContent(out) && htmlHasRichContent(backup)) {
    out = normalizeQuillHtml(backup);
  }
  return out;
}

/** Default max HTML size for API PUT (CloudFront/WAF often blocks multi‑MB pasted pages). */
export const RICH_HTML_API_MAX_CHARS = 350000;

/**
 * Strip embedded images and cap size before save (old-site paste often includes data: URIs / full documents).
 * @returns {{ html: string, truncated: boolean }}
 */
export function prepareRichHtmlForApi(html, { maxChars = RICH_HTML_API_MAX_CHARS } = {}) {
  let s = normalizeQuillHtml(html);
  if (!s) return { html: '', truncated: false };

  s = s.replace(/src\s*=\s*["']data:[^"']*["']/gi, '');
  s = s.replace(/<img[^>]*\ssrc\s*=\s*["']\s*["'][^>]*>/gi, '');
  s = s.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  s = s.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
  s = s.replace(/<li[^>]*>(?:<span[^>]*><\/span>)?(?:<br\s*\/?>|\s|&nbsp;)*<\/li>/gi, '');
  
  // Clean up empty lists that might be left over
  s = s.replace(/<(ul|ol)[^>]*>(?:\s|<br\s*\/?>)*<\/\1>/gi, '');

  let truncated = false;
  if (s.length > maxChars) {
    truncated = true;
    s = s.slice(0, maxChars);
    const last = s.lastIndexOf('</p>');
    if (last > maxChars * 0.75) s = s.slice(0, last + 4);
  }
  return { html: s, truncated };
}
